import { useState } from 'react'
import type { Task, EntityType } from '../../types'
import { useDataStore } from '../../stores/dataStore'
import { Button, Input } from '../ui'
import { toast } from '../ui/Toast'

interface TaskListProps {
  entityType?: EntityType
  entityId?: string
  showAll?: boolean
}

export default function TaskList({ entityType, entityId, showAll = false }: TaskListProps) {
  const { tasks, addTask, toggleTask, deleteTask } = useDataStore()
  const [isAdding, setIsAdding] = useState(false)
  const [title, setTitle] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const filteredTasks = showAll
    ? tasks
    : tasks.filter((t) => t.entityType === entityType && t.entityId === entityId)

  const pendingTasks = filteredTasks.filter((t) => !t.completed)
  const completedTasks = filteredTasks.filter((t) => t.completed)

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast('Title is required', 'error')
      return
    }

    setIsSubmitting(true)
    try {
      await addTask({
        title: title.trim(),
        dueDate: dueDate || undefined,
        completed: false,
        entityType,
        entityId,
      })
      setTitle('')
      setDueDate('')
      setIsAdding(false)
      toast('Task created', 'success')
    } catch {
      toast('Failed to create task', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleToggle = async (id: string) => {
    try {
      await toggleTask(id)
    } catch {
      toast('Failed to update task', 'error')
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteTask(id)
      toast('Task deleted', 'info')
    } catch {
      toast('Failed to delete task', 'error')
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text-primary">Tasks</h3>
        {!isAdding && (
          <Button variant="ghost" size="sm" onClick={() => setIsAdding(true)}>
            + Add
          </Button>
        )}
      </div>

      {isAdding && (
        <div className="bg-[#F5F5F3] rounded-lg p-3 space-y-3">
          <Input
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What needs to be done?"
            autoFocus
          />
          <Input
            label="Due date (optional)"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsAdding(false)
                setTitle('')
                setDueDate('')
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      )}

      {pendingTasks.length === 0 && completedTasks.length === 0 ? (
        <div className="empty-state py-6">
          <p className="empty-state-text">No tasks.</p>
        </div>
      ) : (
        <>
          {pendingTasks.length > 0 && (
            <div className="space-y-1">
              {pendingTasks.map((task) => (
                <TaskItem
                  key={task.id}
                  task={task}
                  onToggle={() => handleToggle(task.id)}
                  onDelete={() => handleDelete(task.id)}
                />
              ))}
            </div>
          )}

          {completedTasks.length > 0 && (
            <div className="space-y-1 opacity-60">
              <p className="text-xs text-text-secondary mt-4">
                Completed ({completedTasks.length})
              </p>
              {completedTasks.map((task) => (
                <TaskItem
                  key={task.id}
                  task={task}
                  onToggle={() => handleToggle(task.id)}
                  onDelete={() => handleDelete(task.id)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

interface TaskItemProps {
  task: Task
  onToggle: () => void
  onDelete: () => void
}

function TaskItem({ task, onToggle, onDelete }: TaskItemProps) {
  const isOverdue = task.dueDate && !task.completed && new Date(task.dueDate) < new Date()

  const formatDueDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
    })
  }

  return (
    <div className="flex items-center gap-3 group bg-[#F5F5F3] rounded-lg p-3">
      <button
        onClick={onToggle}
        className={`
          w-5 h-5 rounded border flex-shrink-0
          flex items-center justify-center
          transition-colors duration-150
          ${task.completed 
            ? 'bg-action-primary border-action-primary text-white' 
            : 'bg-bg-surface border-border hover:border-action-primary'}
        `}
        aria-label={task.completed ? 'Mark as incomplete' : 'Mark as complete'}
      >
        {task.completed && (
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M3 8l4 4 6-8" />
          </svg>
        )}
      </button>

      <div className="flex-1 min-w-0">
        <p className={`text-sm truncate ${task.completed ? 'line-through text-text-secondary' : 'text-text-primary'}`}>
          {task.title}
        </p>
        {task.dueDate && (
          <p className={`text-xs ${isOverdue ? 'text-error' : 'text-text-secondary'}`}>
            Due: {formatDueDate(task.dueDate)}
            {isOverdue && ' (overdue)'}
          </p>
        )}
      </div>

      <button
        onClick={onDelete}
        className="text-text-secondary hover:text-error opacity-0 group-hover:opacity-100 transition-opacity p-1"
        aria-label="Delete task"
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 4l8 8M12 4l-8 8" />
        </svg>
      </button>
    </div>
  )
}
